import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Layers, ImageIcon, Film, Sparkles, Loader2, Pencil, Plus, Trash2, ChevronUp, ChevronDown, Maximize2 } from 'lucide-react';
import Cropper from 'react-cropper';
import "cropperjs/dist/cropper.css";
import api from '../api';
import ImagePickerModal from './ImagePickerModal';
import CoverCropperModal from './CoverCropperModal';
import FullscreenTextEditor from './FullscreenTextEditor';

// 预设分类定义
// 注：text 类型的下拉会在运行时合并已有的全局自定义分类（见 SaveAssetModal）
const CATEGORIES = {
  image: [
    { id: 'role', label: '角色库 (Roles)' },
    { id: 'scene', label: '场景库 (Scenes)' },
    { id: 'prop', label: '道具库 (Props)' }
  ],
  video: [
    { id: 'role', label: '角色库 (Roles)' },
    { id: 'motion', label: '动作库 (Motion)' },
    { id: 'vfx', label: '特效库 (VFX)' }
  ],
  audio: [
    { id: 'music', label: '音乐库 (Music)' },
    { id: 'sfx', label: '音效库 (SFX)' },
    { id: 'voice', label: '音色库 (Voice)' }
  ],
  text: [
    { id: 'prompt', label: '咒语库 (Prompts)' }
  ],
  workflow: [
    { id: 'default', label: '默认分类' }
  ]
};

// 文本节点保存时的初始默认分类 id（仅作为"新打开模态框时下拉的默认选中项"）
export const DEFAULT_SPELL_CATEGORY_ID = 'prompt';

const modalStyle = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
    // 全屏模态须盖住画布左侧工具条(zIndex 10000)和右键菜单(100000)。原 4000 太低，
    // 工具条会穿透显示在对话框上。统一到项目其它全屏 modal 的 999999。
    zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  container: {
    width: '700px',
    maxHeight: '90vh', // 改为最大高度自适应，描述框加高后不会遮住底部按钮
    background: 'var(--bg-panel)', border: '1px solid var(--border-subtle)',
    borderRadius: '16px', boxShadow: '0 25px 60px rgba(0,0,0,0.9)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  header: {
    padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--save-header-bg)'
  },
  body: { flex: 1, display: 'flex', overflow: 'hidden' }, // 分左右栏
  leftCol: { width: '300px', background: 'var(--save-preview-bg)', position: 'relative', borderRight: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  rightCol: { flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' },
  
  footer: {
    padding: '16px 24px', borderTop: '1px solid var(--border-subtle)',
    display: 'flex', justifyContent: 'flex-end', gap: '12px', background: 'var(--bg-panel)'
  },
  label: { display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 'bold' },
  input: {
    width: '100%', padding: '12px', background: '#09090b', border: '1px solid #333',
    borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', transition: 'border 0.2s',
    boxSizing: 'border-box' // 强制包含 padding
  },

};

// 工具：生成视频的一帧并中心裁剪为正方形 Blob
const generateVideoThumbnail = (videoUrl) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = "anonymous";
    video.muted = true;
    
    // 加载元数据后跳转到中间
    video.onloadedmetadata = () => {
      video.currentTime = video.duration > 1 ? 1 : 0; // 取第1秒或开头
    };

    video.onseeked = () => {
      const size = Math.min(video.videoWidth, video.videoHeight);
      const canvas = document.createElement('canvas');
      canvas.width = 200; // 统一定义缩略图尺寸
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      
      // 计算中心裁剪区域
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      
      // 绘制：源(sx, sy, size, size) -> 目标(0, 0, 200, 200)
      ctx.drawImage(video, sx, sy, size, size, 0, 0, 200, 200);
      
      canvas.toBlob((blob) => {
        resolve(blob);
        // 清理
        video.remove();
      }, 'image/jpeg', 0.85);
    };
    
    video.onerror = () => resolve(null);
  });
};

// --- 自定义 Select 组件 ---
// 支持每行附带管理操作（上移/下移/重命名/删除）以及末尾"新建项"按钮
const CustomSelect = ({
  options,
  value,
  onChange,
  onRenameOption,    // (option) => void
  onDeleteOption,    // (option) => void
  onMoveOption,      // (option, direction:-1|1) => void
  onAddOption,       // () => void
  manageable = false, // 显示行内管理图标（重命名/删除/上下移动）
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null); // {left, top, width} 用于 fixed 定位
  const containerRef = useRef(null);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 打开时计算 trigger 位置（fixed 渲染避免被父级 overflow:hidden 裁剪）
  useEffect(() => {
    if (!isOpen) return;
    const updateRect = () => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMenuRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  // 行内小图标按钮
  const IconBtn = ({ icon: Icon, title, onClick, color = '#94a3b8' }) => (
    <span
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '22px', height: '22px', borderRadius: '4px',
        color, flexShrink: 0,
        transition: 'all 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; }}
    >
      <Icon size={12} />
    </span>
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        background: '#09090b',
        border: isOpen ? '1px solid #3b82f6' : '1px solid #333',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        color: '#fff',
        transition: 'border-color 0.2s'
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      {/* Trigger 区域 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px' }}>
         <span>{selectedOption?.label || "请选择"}</span>
         <div style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'flex', color: '#666' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
         </div>
      </div>

      {/* Dropdown 列表 — position:fixed 避免被 modal body 的 overflow:hidden 裁剪 */}
      {isOpen && menuRect && (
        <div style={{
           position: 'fixed',
           left: menuRect.left,
           top: menuRect.top,
           width: menuRect.width,
           background: '#18181b',
           border: '1px solid #333',
           borderRadius: '8px',
           zIndex: 5000,
           boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
           overflow: 'hidden',
           maxHeight: '280px',
           overflowY: 'auto'
        }}>
            {options.map((opt, idx) => {
              const showManage = manageable && !opt.locked;
              const isFirst = idx === 0;
              const isLast = idx === options.length - 1;
              return (
                <div
                  key={opt.value}
                  onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt.value);
                      setIsOpen(false);
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#27272a'}
                  onMouseLeave={e => e.currentTarget.style.background = opt.value === value ? '#27272a' : 'transparent'}
                  style={{
                      padding: '8px 10px',
                      background: opt.value === value ? '#27272a' : 'transparent',
                      color: opt.value === value ? '#3b82f6' : '#cbd5e1',
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                  }}
                >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                    {showManage && onMoveOption && (
                      <>
                        <IconBtn
                          icon={ChevronUp}
                          title="上移"
                          onClick={() => !isFirst && onMoveOption(opt, -1)}
                          color={isFirst ? '#444' : '#94a3b8'}
                        />
                        <IconBtn
                          icon={ChevronDown}
                          title="下移"
                          onClick={() => !isLast && onMoveOption(opt, 1)}
                          color={isLast ? '#444' : '#94a3b8'}
                        />
                      </>
                    )}
                    {showManage && onRenameOption && (
                      <IconBtn icon={Pencil} title="重命名" onClick={() => { setIsOpen(false); onRenameOption(opt); }} />
                    )}
                    {showManage && onDeleteOption && (
                      <IconBtn icon={Trash2} title="删除" color="#fca5a5" onClick={() => { setIsOpen(false); onDeleteOption(opt); }} />
                    )}
                </div>
              );
            })}

            {onAddOption && (
              <div
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); onAddOption(); }}
                onMouseEnter={e => e.currentTarget.style.background = '#27272a'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#3b82f6',
                  fontWeight: 500,
                }}
              >
                <Plus size={14} />
                <span>新建分类</span>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default function SaveAssetModal({
  isOpen,
  nodeData,
  onClose,
  onSave,
  isSuperAdmin,
  // 已存在的全局咒语分类列表（按当前排序），由 EditorPage 派生（含默认 'prompt'）
  globalSpellCategories = [],
  // 重命名分类（仅 super_admin），(oldId, newId) => Promise；不传则不显示
  onRenameSpellCategory,
  // 删除分类（仅 super_admin），(id) => Promise
  onDeleteSpellCategory,
  // 重排分类（仅 super_admin），(newOrderArray) => Promise
  onReorderSpellCategories,
  projectId, // 🆕 用于加载当前画板的图片
}) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState(''); // 🆕 描述词：整段进提示词传给模型
  const [showDescFullscreen, setShowDescFullscreen] = useState(false); // 🆕 描述词全屏编辑
  const [videoThumbUrl, setVideoThumbUrl] = useState(null);
  const [videoThumbBlob, setVideoThumbBlob] = useState(null);
  const [saving, setSaving] = useState(false); // 🛡️ 防止重复点击
  const [isGlobalSpell, setIsGlobalSpell] = useState(false); // 🌍 是否部署为全局咒语
  const [isEncryptedSpell, setIsEncryptedSpell] = useState(false); // 🔒 是否加密
  // 本会话内新建的咒语分类（暂存：还没保存到 DB 之前下拉里也能看到）
  const [pendingNewCategories, setPendingNewCategories] = useState([]);
  const cropperRef = useRef(null);
  const videoThumbUrlRef = useRef(null); // 🛡️ 追踪最新 blob URL，避免闭包陈旧导致泄漏

  // 🆕 工作流封面设置
  const [showWorkflowCoverPicker, setShowWorkflowCoverPicker] = useState(false);
  const [workflowImages, setWorkflowImages] = useState([]);
  const [workflowCoverCropUrl, setWorkflowCoverCropUrl] = useState(null);
  const [workflowCoverBlob, setWorkflowCoverBlob] = useState(null);
  const workflowCropperRef = useRef(null); // 🆕 工作流裁剪器引用

  // 🆕 编辑资产模式
  const [currentImageSrc, setCurrentImageSrc] = useState(null); // 当前显示的图片源
  const [replacedImageFile, setReplacedImageFile] = useState(null); // 替换的新图片文件
  const fileInputRef = useRef(null); // 文件选择器引用

  // ⚠️ 这里原来有一套「动态分类系统」的前端代码（调 GET /api/asset-categories，
  //    把返回结果按 workflow/image/video/audio/text 分组，用于预选默认分类）。
  //    2026-09-29 整段移除，原因：
  //      · 那个后端路由（server/routes/categories.js）属于 production/huanshe 的
  //        动态分类功能，从没进过 production/cineart —— 主站 app.js 不挂载它，
  //        前端这段代码在主站上必然撞 404，每次打开面板都往控制台刷一条红字。
  //      · 下拉框的选项其实来自本文件顶部的硬编码常量 CATEGORIES（见 currentTypeCats），
  //        跟这个接口无关；接口数据只用于「预选默认项」，而默认项在下面的初始化
  //        useEffect 里已经用 CATEGORIES 设好了（defaultCat）。
  //    所以这是纯减法：行为不变，少一条 404。
  //
  //    ⚠️ 生产库里 asset_categories 表和 9 条种子数据仍然保留（未删，删表无必要且有风险）。
  //       若将来要恢复这套功能，注意有 123 条资产的分类不在那 9 条里（motion 80 个视频、
  //       default、prompt、vfx、voice，以及手工建的中文咒语分类），而 AssetDrawer 是
  //       category 精确匹配且没有「未分类」兜底 —— 不补齐分类数据会让这些资产从界面消失。

  // （原来这里还有一个「等分类加载完成后预选默认项」的 useEffect，依赖上面那五个
  //   已删除的 state。因为接口在主站恒 404，那五个 state 恒为空数组，这个 effect
  //   实际上从来没设置过任何值 —— 默认分类一直由下面初始化 effect 里的
  //   CATEGORIES[type]?.[0]?.id 决定。一并删除，行为不变。）

  // 初始化

  useEffect(() => {
    let cancelled = false; // 🛡️ 防止组件关闭后仍调用 setState
    if (isOpen && nodeData) {
      setSaving(false); // 🛡️ 重新打开时重置保存状态
      setIsGlobalSpell(false); // 重新打开时默认不部署全局
      setIsEncryptedSpell(false); // 重新打开时默认不加密
      setPendingNewCategories([]); // 重置本次会话内新建的分类
      setTitle(nodeData.title || '未命名资产');
      setDescription(nodeData.description || ''); // 🆕 已有描述词则回填
      const type = nodeData.type === 'text' ? 'text' : nodeData.type; // 归一化类型
      const defaultCat = nodeData.category || CATEGORIES[type]?.[0]?.id || 'misc';
      setCategory(defaultCat);

      // 🆕 重置编辑模式状态
      setReplacedImageFile(null);
      const initialSrc = nodeData.localCacheUrl || nodeData.output || nodeData.url;
      setCurrentImageSrc(initialSrc);

      // 🆕 重置工作流封面状态
      setShowWorkflowCoverPicker(false);
      setWorkflowImages([]);
      setWorkflowCoverCropUrl(null);
      setWorkflowCoverBlob(null);

      // 如果是视频，自动生成缩略图（优先用本地缓存 blob URL，避免跨域问题）
      const videoSrc = nodeData.localCacheUrl || nodeData.output;
      if (nodeData.type === 'video' && videoSrc) {
        generateVideoThumbnail(videoSrc).then(blob => {
            if (blob && !cancelled) {
                setVideoThumbBlob(blob);
                const url = URL.createObjectURL(blob);
                videoThumbUrlRef.current = url;
                setVideoThumbUrl(url);
            }
        });
      }
    }
    return () => {
        cancelled = true;
        if (videoThumbUrlRef.current) {
            URL.revokeObjectURL(videoThumbUrlRef.current);
            videoThumbUrlRef.current = null;
        }
    }
  }, [isOpen, nodeData]);

  // 【修复】监听分类变化，强制更新裁剪框比例
  useEffect(() => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const isRole = category === 'role';
      cropperRef.current.cropper.setAspectRatio(isRole ? 86 / 142 : 1);
    }
  }, [category]);

  // 🆕 处理工作流封面选择
  const handleSetWorkflowCover = async () => {
    if (!projectId) {
      console.warn('无法获取当前图板 ID');
      return;
    }
    try {
      const data = await api.get(`/api/projects/${projectId}`);
      const nodes = data.nodes || [];
      const images = nodes
        .filter(n => n.data?.type === 'image' && (n.data?.localCacheUrl || n.data?.output))
        .map(n => ({
          url: n.data.localCacheUrl || n.data.output,
          title: n.data.title || '未命名'
        }));

      if (images.length === 0) {
        alert('当前图板中没有图片资产');
        return;
      }

      setWorkflowImages(images);
      setShowWorkflowCoverPicker(true);
    } catch (err) {
      console.error('加载图片失败:', err);
    }
  };

  const handleSelectWorkflowCoverImage = (url) => {
    setShowWorkflowCoverPicker(false);
    setWorkflowCoverCropUrl(url);
  };

  // 🆕 在保存时从裁剪器获取 blob（如果正在裁剪中）
  const getWorkflowCoverBlobFromCropper = () => {
    return new Promise((resolve) => {
      if (!workflowCoverCropUrl) {
        // 没有在裁剪，直接用已有的 blob
        resolve(workflowCoverBlob);
        return;
      }

      // 正在裁剪，从裁剪器获取
      const cropper = workflowCropperRef.current?.cropper;
      if (!cropper) {
        resolve(null);
        return;
      }

      cropper.getCroppedCanvas({
        width: 512,
        height: 512,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      }).toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  // 🆕 处理替换图片
  const handleReplaceImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 🆕 处理文件选择
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    // 读取文件并更新预览
    const reader = new FileReader();
    reader.onload = (event) => {
      const newSrc = event.target.result;
      setCurrentImageSrc(newSrc);
      setReplacedImageFile(file);

      // 如果是裁剪器，需要重新初始化
      if (cropperRef.current?.cropper) {
        cropperRef.current.cropper.replace(newSrc);
      }
    };
    reader.readAsDataURL(file);

    // 清空 input 值，允许选择同一文件
    e.target.value = '';
  };

  if (!isOpen || !nodeData) return null;

  // 🆕 判断是否为编辑模式
  const isEditingAsset = !!nodeData.isEditingAsset;
  const editingAssetId = nodeData.editingAssetId;

  const handleSave = async () => {
    if (saving) return; // 🛡️ 防止重复点击
    setSaving(true);

    // 🆕 编辑模式：调用编辑回调
    if (isEditingAsset && editingAssetId) {
      let finalThumbnailBlob = null;
      let finalMiniThumbnailBlob = null;
      const isRole = category === 'role';
      const aspectWidth = 200;
      const aspectHeight = isRole ? Math.round(200 * (142 / 86)) : 200;

      // 包装调用
      const callOnSave = async (payload) => {
        try {
          await onSave(payload);
        } finally {
          setSaving(false);
        }
      };

      // 如果替换了图片，需要重新生成缩略图
      if (replacedImageFile) {
        if (nodeData.type === 'image' && cropperRef.current) {
          const canvas = cropperRef.current.cropper.getCroppedCanvas({
            width: aspectWidth, height: aspectHeight,
            imageSmoothingQuality: 'high'
          });
          if (canvas) {
            await new Promise((resolve) => {
              canvas.toBlob((blob) => {
                finalThumbnailBlob = blob;
                // 同时生成 80px 迷你缩略图
                const miniCanvas = cropperRef.current.cropper.getCroppedCanvas({
                  width: 80, height: 80,
                  imageSmoothingQuality: 'high'
                });
                miniCanvas.toBlob((miniBlob) => {
                  finalMiniThumbnailBlob = miniBlob;
                  resolve();
                }, 'image/jpeg', 0.80);
              }, 'image/jpeg', 0.85);
            });
          }
        } else if (nodeData.type === 'video') {
          // 视频：重新生成缩略图
          finalThumbnailBlob = videoThumbBlob;
          if (videoThumbBlob) {
            const img = new Image();
            const objectUrl = URL.createObjectURL(videoThumbBlob);
            await new Promise((resolve) => {
              img.onload = () => {
                const miniCanvas = document.createElement('canvas');
                miniCanvas.width = 80;
                miniCanvas.height = 80;
                const ctx = miniCanvas.getContext('2d');
                ctx.drawImage(img, 0, 0, 80, 80);
                miniCanvas.toBlob((miniBlob) => {
                  URL.revokeObjectURL(objectUrl);
                  finalMiniThumbnailBlob = miniBlob;
                  resolve();
                }, 'image/jpeg', 0.80);
              };
              img.src = objectUrl;
            });
          }
        }

        callOnSave({
          assetId: editingAssetId,
          title,
          description,
          thumbnailBlob: finalThumbnailBlob,
          miniThumbnailBlob: finalMiniThumbnailBlob,
          newImageFile: replacedImageFile, // 新图片文件
          isEditing: true,
        });
        return;
      }

      // 没有替换图片，只更新标题和描述
      callOnSave({
        assetId: editingAssetId,
        title,
        description,
        isEditing: true,
      });
      return;
    }

    // 原有的新建逻辑
    let finalThumbnailBlob = null;
    let finalMiniThumbnailBlob = null; // 🆕 迷你缩略图
    const isRole = category === 'role';
    const aspectWidth = 200;
    const aspectHeight = isRole ? Math.round(200 * (142 / 86)) : 200; // 角色保持 86:142 比例，其他正方形

    // 包装 onSave 调用：await 完成后恢复按钮状态（仅在失败时需要，成功时 modal 会关闭）
    const callOnSave = async (payload) => {
      try {
        await onSave(payload);
      } finally {
        setSaving(false);
      }
    };

    if (nodeData.type === 'image' && cropperRef.current) {
        // 获取裁剪后的标准缩略图（200px）
        const canvas = cropperRef.current.cropper.getCroppedCanvas({
            width: aspectWidth, height: aspectHeight,
            imageSmoothingQuality: 'high'
        });
        if (canvas) {
            canvas.toBlob((blob) => {
                // 同时生成 80px 迷你缩略图
                const miniCanvas = cropperRef.current.cropper.getCroppedCanvas({
                    width: 80, height: 80,
                    imageSmoothingQuality: 'high'
                });
                miniCanvas.toBlob((miniBlob) => {
                    callOnSave({
                      title,
                      description,
                      category,
                      type: nodeData.type,
                      thumbnailBlob: blob,
                      miniThumbnailBlob: miniBlob, // 🆕 迷你缩略图
                      bgColor: nodeData.bgColor,
                      isGlobal: !!isGlobalSpell,
                      isEncrypted: !!(isGlobalSpell && isEncryptedSpell),
                    });
                }, 'image/jpeg', 0.80);
            }, 'image/jpeg', 0.85);
            return;
        }
    } else if (nodeData.type === 'video') {
         finalThumbnailBlob = videoThumbBlob;
         // 🆕 视频：从标准缩略图再生成 80px 迷你版
         if (videoThumbBlob) {
             const img = new Image();
             const objectUrl = URL.createObjectURL(videoThumbBlob);
             await new Promise((resolve) => {
                 img.onload = () => {
                     const miniCanvas = document.createElement('canvas');
                     miniCanvas.width = 80;
                     miniCanvas.height = 80;
                     const ctx = miniCanvas.getContext('2d');
                     ctx.drawImage(img, 0, 0, 80, 80);
                     miniCanvas.toBlob((miniBlob) => {
                         URL.revokeObjectURL(objectUrl);
                         finalThumbnailBlob = videoThumbBlob;
                         finalMiniThumbnailBlob = miniBlob;
                         resolve();
                     }, 'image/jpeg', 0.80);
                 };
                 img.src = objectUrl;
             });
         }
    } else if (nodeData.type === 'workflow' || nodeData.type === 'text') {
         // 🆕 工作流/咒语：从裁剪器获取当前裁剪结果
         finalThumbnailBlob = await getWorkflowCoverBlobFromCropper();
         // 🆕 同时生成 80px 迷你版
         if (finalThumbnailBlob) {
             const img = new Image();
             const objectUrl = URL.createObjectURL(finalThumbnailBlob);
             await new Promise((resolve) => {
                 img.onload = () => {
                     const miniCanvas = document.createElement('canvas');
                     miniCanvas.width = 80;
                     miniCanvas.height = 80;
                     const ctx = miniCanvas.getContext('2d');
                     ctx.drawImage(img, 0, 0, 80, 80);
                     miniCanvas.toBlob((miniBlob) => {
                         URL.revokeObjectURL(objectUrl);
                         finalMiniThumbnailBlob = miniBlob;
                         resolve();
                     }, 'image/jpeg', 0.80);
                 };
                 img.src = objectUrl;
             });
         }
    }

    callOnSave({
      title,
      description,
      category,
      type: nodeData.type,
      thumbnailBlob: finalThumbnailBlob,
      miniThumbnailBlob: finalMiniThumbnailBlob, // 🆕 迷你缩略图
      bgColor: nodeData.bgColor, // 【新增】保存背景色
      isGlobal: !!isGlobalSpell,
      isEncrypted: !!(isGlobalSpell && isEncryptedSpell),
    });
  };

  const currentTypeCats = CATEGORIES[nodeData.type] || [];
  const isImageByCheck = nodeData.type === 'image';
  const isVideoByCheck = nodeData.type === 'video';
  const isAudioByCheck = nodeData.type === 'audio';
  const isRoleCategory = category === 'role';
  const isTextType = nodeData.type === 'text';
  const isWorkflowType = nodeData.type === 'workflow';
  const isEditingCover = nodeData?.isEditingCover; // 🆕 编辑封面模式


  // ── 咒语库分类下拉选项 ──
  // text 类型：globalSpellCategories（按当前持久化排序）+ 本会话新建的暂存分类
  // 'prompt' 仅在显示层做友好 label 替换
  const promptDisplay = (id) => (id === DEFAULT_SPELL_CATEGORY_ID ? '咒语库 (Prompts)' : id);
  const categoryOptions = (() => {
    if (!isTextType) {
      return currentTypeCats.length > 0
        ? currentTypeCats.map(c => ({ value: c.id, label: c.label, locked: true }))
        : [{ value: 'misc', label: '杂项 (Misc)', locked: true }];
    }
    const seen = new Set();
    const opts = [];
    // 已存在的分类（按服务端给的排序）
    [...globalSpellCategories].forEach(cat => {
      if (!cat || seen.has(cat)) return;
      seen.add(cat);
      opts.push({ value: cat, label: promptDisplay(cat) });
    });
    // 本次会话内新建的分类（追加在末尾）
    pendingNewCategories.forEach(cat => {
      if (!cat || seen.has(cat)) return;
      seen.add(cat);
      opts.push({ value: cat, label: cat });
    });
    // 兜底：合并后完全为空时才补一个 prompt
    if (opts.length === 0) {
      opts.push({ value: DEFAULT_SPELL_CATEGORY_ID, label: promptDisplay(DEFAULT_SPELL_CATEGORY_ID) });
    }
    return opts;
  })();

  // 新建分类
  const handleAddCategory = () => {
    const name = window.prompt('请输入新分类名称');
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (categoryOptions.some(o => o.value === trimmed || o.label === trimmed)) {
      window.alert('分类已存在');
      setCategory(trimmed);
      return;
    }
    setPendingNewCategories(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
    setCategory(trimmed);
  };

  // 重命名分类
  const handleRenameCategory = async (opt) => {
    if (!onRenameSpellCategory || !opt) return;
    const newName = window.prompt('重命名分类', opt.value === DEFAULT_SPELL_CATEGORY_ID ? '咒语库 (Prompts)' : opt.value);
    if (!newName) return;
    const trimmed = newName.trim();
    if (!trimmed || trimmed === opt.value) return;
    if (categoryOptions.some(o => o.value === trimmed)) {
      window.alert('已存在同名分类');
      return;
    }
    try {
      await onRenameSpellCategory(opt.value, trimmed);
      setPendingNewCategories(prev => prev.map(c => c === opt.value ? trimmed : c));
      if (category === opt.value) setCategory(trimmed);
    } catch (err) {
      window.alert('重命名失败：' + (err?.message || '请稍后重试'));
    }
  };

  // 删除分类（拒绝非空，由后端校验；本地分类直接从 pending 移除）
  const handleDeleteCategory = async (opt) => {
    if (!opt) return;
    // 至少保留一个分类，不允许删到全空
    if (categoryOptions.length <= 1) {
      window.alert('至少保留一个分类');
      return;
    }
    const isPending = pendingNewCategories.includes(opt.value);
    if (isPending) {
      // 本会话内新建但还没保存到 DB，直接移除
      setPendingNewCategories(prev => prev.filter(c => c !== opt.value));
      if (category === opt.value) {
        const remain = categoryOptions.find(o => o.value !== opt.value);
        if (remain) setCategory(remain.value);
      }
      return;
    }
    if (!onDeleteSpellCategory) return;
    if (!window.confirm(`确认删除分类「${opt.label}」？\n（仅当分类为空时可删除）`)) return;
    try {
      await onDeleteSpellCategory(opt.value);
      if (category === opt.value) {
        const remain = categoryOptions.find(o => o.value !== opt.value);
        if (remain) setCategory(remain.value);
      }
    } catch (err) {
      window.alert('删除失败：' + (err?.message || '请稍后重试'));
    }
  };

  // 上下移动分类
  const handleMoveCategory = async (opt, direction) => {
    if (!onReorderSpellCategories) return;
    const ids = categoryOptions.map(o => o.value);
    const idx = ids.indexOf(opt.value);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[idx], next[target]] = [next[target], next[idx]];
    try {
      await onReorderSpellCategories(next);
    } catch (err) {
      window.alert('调整顺序失败：' + (err?.message || '请稍后重试'));
    }
  };

  return (
    <div style={modalStyle.overlay}>
      <style>{`
        .cropper-point.point-e, .cropper-point.point-n, .cropper-point.point-w, .cropper-point.point-s { display: none !important; }
        .cropper-line { display: none !important; }
        .cropper-view-box { outline: 1px solid #fff; outline-color: rgba(255, 255, 255, 0.75); }
      `}</style>
      <div style={modalStyle.container}>
        {/* Header */}
        <div style={modalStyle.header}>
          <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
             {isImageByCheck ? <ImageIcon size={20} color="#3b82f6" /> : isVideoByCheck ? <Film size={20} color="#22c55e" /> : <Layers size={20} color="#f97316" />}
             <span style={{fontWeight:'bold', color:'var(--text-primary)', letterSpacing:'1px'}}>
               {isEditingCover ? '编辑封面' : (isEditingAsset ? '编辑资产' : '保存到资料库')}
             </span>
          </div>
          <div onClick={onClose} style={{cursor:'pointer', color:'var(--text-secondary)'}}><X size={20} /></div>
        </div>

        <div style={modalStyle.body}>
            {/* Left: Preview / Cropper */}
            <div style={modalStyle.leftCol}>
                {/* 🆕 隐藏的文件选择器 */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {isImageByCheck ? (
                    <Cropper
                        src={currentImageSrc}
                        style={{ height: '100%', width: '100%' }}
                        aspectRatio={isRoleCategory ? 86 / 142 : 1} // 角色用长方形，其他正方形
                        guides={false}
                        center={false}
                        // viewMode 2 而非 1：1 只约束「裁剪框不超出图」，没管图相对容器的大小，
                        // 滚轮往后拉能把图缩成黑框里的一小片，裁剪框跟着缩到没法用。
                        // 2 要求图至少填满容器一边，缩到贴合就停住。
                        // 不用 3 —— 3 要求完全覆盖容器，左栏是竖长条，横图会被强制裁掉左右两边。
                        viewMode={2}
                        dragMode="move"
                        ref={cropperRef}
                        background={false}
                        autoCropArea={0.8}
                        toggleDragModeOnDblclick={false}
                    />
                ) : isVideoByCheck ? (
                    videoThumbUrl ?
                    <img src={videoThumbUrl} style={{width:'100%', height:'100%', objectFit:'contain'}} alt="Video Thumbnail" /> :
                    <div style={{color:'#666', fontSize:'12px'}}>正在生成封面...</div>
                ) : isWorkflowType || isTextType ? (
                    // 🎨 工作流/咒语封面：两种状态
                    <>
                        {workflowCoverCropUrl ? (
                            // 状态 1: 裁剪模式 - 显示裁剪器
                            <Cropper
                                src={workflowCoverCropUrl}
                                style={{ height: '100%', width: '100%' }}
                                aspectRatio={1} // 🔲 正方形裁剪
                                guides={true}
                                center={true}
                                viewMode={2} // 同上：防止滚轮把图缩得比容器还小
                                dragMode="move"
                                ref={workflowCropperRef}
                                background={false}
                                autoCropArea={0.8}
                                toggleDragModeOnDblclick={false}
                            />
                        ) : (
                            // 状态 2: 未设置/无裁剪 - 显示占位图
                            <div style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:'#666'}}>
                                <Sparkles size={48} strokeWidth={1} />
                            </div>
                        )}

                        {/* 底部按钮：始终显示「选择封面图片」 */}
                        <button
                            onClick={handleSetWorkflowCover}
                            style={{
                                position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
                                padding: '4px 12px', borderRadius: '4px',
                                background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '400',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
                        >
                            选择封面图片
                        </button>
                    </>
                ) : (
                    <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'12px', color:'#666'}}>
                        <Sparkles size={48} strokeWidth={1} />
                        <span style={{fontSize:'12px'}}>无预览可用</span>
                    </div>
                )}

                {/* 🆕 替换图片按钮 - 编辑资产模式下显示 */}
                {isEditingAsset && (isImageByCheck || isVideoByCheck) && (
                  <button
                    onClick={handleReplaceImage}
                    style={{
                      position: 'absolute', bottom: '8px', left: '8px',
                      padding: '6px 12px', borderRadius: '6px',
                      background: 'rgba(59, 130, 246, 0.9)', border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff', fontSize: '11px', cursor: 'pointer', fontWeight: '500',
                      display: 'flex', alignItems: 'center', gap: '4px',
                      transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 1)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.9)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <Plus size={12} />
                    替换图片
                  </button>
                )}

                {/* 浮动提示 */}
                {isImageByCheck && !isEditingAsset && (
                    <div style={{position:'absolute', bottom:'10px', left:'0', right:'0', textAlign:'center', pointerEvents:'none'}}>
                        <span style={{background:'rgba(0,0,0,0.6)', color:'#fff', padding:'4px 8px', borderRadius:'12px', fontSize:'10px'}}>
                            裁剪缩略图
                        </span>
                    </div>
                )}
            </div>

            {/* Right: Inputs */}
            <div style={modalStyle.rightCol}>
                <div>
                    <label style={modalStyle.label}>标题 (TITLE)</label>
                    <input
                        style={{...modalStyle.input, opacity: isEditingCover ? 0.6 : 1, cursor: isEditingCover ? 'not-allowed' : 'text'}}
                        value={title}
                        onChange={(e) => !isEditingCover && setTitle(e.target.value)}
                        autoFocus={!isEditingCover}
                        placeholder="输入名称..."
                        disabled={isEditingCover}
                    />
                </div>

                {/* 所属分类下拉
                    - 普通用户保存 text 时：分类对他们无意义（无"部署全局"权限），直接隐藏
                      默认 category='prompt' 落到自己后台的咒语库
                    - 其他类型 / super_admin：正常显示
                    - 🆕 编辑模式：禁用分类选择 */}
                {!(isTextType && !isSuperAdmin) && (
                  <div style={{opacity: (isEditingCover || isEditingAsset) ? 0.6 : 1, pointerEvents: (isEditingCover || isEditingAsset) ? 'none' : 'auto'}}>
                      <label style={modalStyle.label}>所属分类 (CATEGORY) {isEditingAsset && <span style={{color:'#666', fontSize:'10px'}}>（不可修改）</span>}</label>
                      <CustomSelect
                          options={categoryOptions}
                          value={category}
                          onChange={setCategory}
                          // 所有类型统一使用 CategoryManagement 后台管理，不再提供内嵌管理功能
                          manageable={false}
                          disabled={isEditingCover || isEditingAsset}
                      />
                  </div>
                )}

                {/* 🆕 描述词（图片/视频/音频）：连到下游生成节点时整段作为提示词传给模型 */}
                {(isImageByCheck || isVideoByCheck || isAudioByCheck) && (
                  <div style={{opacity: isEditingCover ? 0.6 : 1, pointerEvents: isEditingCover ? 'none' : 'auto'}}>
                      <label style={modalStyle.label}>描述 (DESCRIPTION)</label>
                      {/* resize:'none' —— 右下角原生拉伸手柄没必要（要大就点全屏），
                          留着还会和全屏按钮抢同一个角落。 */}
                      <div style={{ position: 'relative' }}>
                        <textarea
                            style={{
                              ...modalStyle.input,
                              height: '160px', resize: 'none',
                              padding: '10px 10px 40px',
                              lineHeight: 1.5, fontFamily: 'inherit',
                            }}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="描述这张图的内容，会作为提示词传给模型…"
                            disabled={isEditingCover}
                        />
                        {/* 全屏编辑：进去有文本整理 + 按住说话 */}
                        <div
                          onClick={() => { if (!isEditingCover) setShowDescFullscreen(true); }}
                          title="全屏编辑"
                          style={{
                            position: 'absolute', right: 8, bottom: 8,
                            width: 28, height: 28, borderRadius: 6,
                            background: 'var(--bg-hover)',
                            border: '1px solid var(--border-strong)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(4px)',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.borderColor = 'var(--text-hover)';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.borderColor = 'var(--border-strong)';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <Maximize2 size={14} color="var(--text-secondary)" strokeWidth={2} />
                        </div>
                      </div>
                      <FullscreenTextEditor
                        isOpen={showDescFullscreen}
                        onClose={() => setShowDescFullscreen(false)}
                        value={description}
                        onChange={setDescription}
                        title="编辑描述词"
                      />
                  </div>
                )}

                {/* 🌍/🔒 全局咒语选项（仅 super_admin 可见，仅咒语/工作流类型，编辑封面时隐藏） */}
                {!isEditingCover && isSuperAdmin && (nodeData.type === 'text' || nodeData.type === 'workflow') && (
                  <>
                    <div
                      onClick={() => {
                        const next = !isGlobalSpell;
                        setIsGlobalSpell(next);
                        if (!next) setIsEncryptedSpell(false);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        cursor: 'pointer', padding: '8px 12px', marginTop: '8px',
                        borderRadius: '8px', fontSize: '13px',
                        background: isGlobalSpell ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        border: isGlobalSpell ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                        color: isGlobalSpell ? '#3b82f6' : '#888',
                        transition: 'all 0.2s', userSelect: 'none'
                      }}
                    >
                      <div style={{
                        width: '14px', height: '14px', borderRadius: '3px',
                        background: isGlobalSpell ? '#3b82f6' : 'transparent',
                        border: isGlobalSpell ? '2px solid #3b82f6' : '2px solid #555',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s', flexShrink: 0
                      }}>
                        {isGlobalSpell && <div style={{ width: '6px', height: '6px', borderRadius: '1px', background: '#fff' }} />}
                      </div>
                      {nodeData.type === 'workflow' ? '部署为全局工作流' : '部署为全局咒语'}
                    </div>

                    {/* 🔒 加密选项：仅咒语类型显示，工作流不显示 */}
                    {nodeData.type === 'text' && (
                      <div
                        onClick={() => { if (isGlobalSpell) setIsEncryptedSpell(!isEncryptedSpell); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          cursor: isGlobalSpell ? 'pointer' : 'not-allowed',
                          padding: '8px 12px', marginTop: '8px',
                          borderRadius: '8px', fontSize: '13px',
                          background: isEncryptedSpell ? 'rgba(234, 179, 8, 0.12)' : 'transparent',
                          border: isEncryptedSpell ? '1px solid rgba(234, 179, 8, 0.4)' : '1px solid rgba(255,255,255,0.1)',
                          color: isGlobalSpell ? (isEncryptedSpell ? '#fbbf24' : '#aaa') : '#666',
                          opacity: isGlobalSpell ? 1 : 0.6,
                          transition: 'all 0.2s', userSelect: 'none'
                        }}
                      >
                        <div style={{
                          width: '14px', height: '14px', borderRadius: '3px',
                          background: isEncryptedSpell ? '#f59e0b' : 'transparent',
                          border: isEncryptedSpell ? '2px solid #f59e0b' : '2px solid #555',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.2s', flexShrink: 0
                        }}>
                          {isEncryptedSpell && <div style={{ width: '6px', height: '6px', borderRadius: '1px', background: '#fff' }} />}
                        </div>
                        加密咒语
                      </div>
                    )}
                  </>
                )}

                {/* 🔒 非管理员保存加密咒语时的提示 */}
                {!isSuperAdmin && nodeData.isSystemSpell && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', marginTop: '8px',
                    borderRadius: '8px', fontSize: '13px',
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    color: '#fbbf24',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    保存后仍为加密锁定状态
                  </div>
                )}

                <div style={{marginTop:'auto', fontSize:'12px', color:'var(--text-muted)', lineHeight:'1.5'}}>
                   提示：
                   {isImageByCheck ? "左侧裁剪的内容将作为后台列表中的缩略图（Thumbnail），拖入图板时仍使用原图。" :
                    isVideoByCheck ? "已自动截取视频中间画面作为封面缩略图。" :
                    (isWorkflowType || isTextType) ? "点击上方按钮从当前图板选择图片作为封面。" :
                    "该类型资产暂不支持自定义封面。"}
                </div>
            </div>
        </div>

        {/* Footer */}
        <div style={modalStyle.footer}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--border-strong)',
            color: 'var(--text-body)', padding: '8px 20px', borderRadius: '6px', cursor:'pointer'
          }}>
            取消
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            background: saving ? '#555' : '#3b82f6', border: 'none',
            color: '#fff', padding: '8px 24px', borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer', fontWeight:'bold', display:'flex', alignItems:'center', gap:'8px',
            boxShadow: saving ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.4)',
            opacity: saving ? 0.7 : 1, transition: 'all 0.2s'
          }}>
            {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
            {saving ? '保存中...' : (isEditingCover ? '确认更新' : (isEditingAsset ? '更新资产' : '确认入库'))}
          </button>
        </div>
      </div>

      {/* 🆕 工作流封面图片选择器 */}
      {showWorkflowCoverPicker && (
        <ImagePickerModal
          images={workflowImages}
          onSelect={handleSelectWorkflowCoverImage}
          onClose={() => setShowWorkflowCoverPicker(false)}
        />
      )}
    </div>
  );
}